import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../src/auth/useAuth";
import { plutoWritingPreferencesQueryKey, type PlutoWritingPreferences } from "./pluto-writing-preferences";
import useApi from "./useApi";

export const usePlutoWritingPreferences = () => {
  const { instance } = useApi();
  const { clientId, isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = plutoWritingPreferencesQueryKey(clientId, user?.id ?? null);

  const query = useQuery<PlutoWritingPreferences>({
    queryKey,
    queryFn: async ({ signal }) => {
      const response = await instance.get<PlutoWritingPreferences>("/v3/pluto/writing-preferences", { signal });
      return response.data;
    },
    enabled: isAuthenticated,
    retry: 1,
    staleTime: 60_000,
  });

  const save = useMutation({
    mutationFn: async (instructions: string) => {
      const response = await instance.put<PlutoWritingPreferences>("/v3/pluto/writing-preferences", {
        instructions,
      });
      return response.data;
    },
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKey, preferences);
    },
  });

  const clear = useMutation({
    mutationFn: async () => {
      const response = await instance.delete<PlutoWritingPreferences>("/v3/pluto/writing-preferences");
      return response.data;
    },
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKey, preferences);
    },
  });

  return { query, save, clear };
};

export default usePlutoWritingPreferences;
